import { useCallback, useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import { useApi } from '@hero/context';
import type { ReferralTrackingCreateAPI } from '@hero/api';
import { capture, getReferralSuperProperties, type Business } from '@hero/core';
import {
  getReferralCode,
  getReferralCodeCurrent,
  getReferralVisitorId,
  readReferralCodeFromSearch,
  storeReferralCode,
} from './referralStorage';

export interface UseReferralCaptureOptions {
  /** Which line this surface belongs to; scopes `closest_tracking`. */
  business: Business;
  /** UI language, stamped on the hit for the admin list. */
  language?: string;
  /**
   * Set `false` to skip the automatic `view` hit (e.g. inside an
   * authenticated area where only CTA clicks matter). Default `true`.
   */
  trackView?: boolean;
}

type TrackingContext = NonNullable<ReferralTrackingCreateAPI['req']['context']>;

const readUtm = (params: URLSearchParams): TrackingContext['utm'] => {
  const utm = {
    source: params.get('utm_source') ?? undefined,
    medium: params.get('utm_medium') ?? undefined,
    campaign: params.get('utm_campaign') ?? undefined,
    term: params.get('utm_term') ?? undefined,
    content: params.get('utm_content') ?? undefined,
  };
  return Object.values(utm).some(Boolean) ? utm : undefined;
};

/** PostHog's anonymous / identified id for this browser, when the SDK is up. */
const posthogDistinctId = (): string | undefined => {
  try {
    return posthog.__loaded ? posthog.get_distinct_id() : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Stamp the referral super-properties on every later PostHog event this
 * browser sends. Idempotent; no-op when PostHog is not initialised.
 */
export const registerReferralSuperProperties = (business?: Business): void => {
  const code = getReferralCode();
  if (!code) return;
  try {
    if (!posthog.__loaded) return;
    posthog.register(
      getReferralSuperProperties({
        code,
        currentCode: getReferralCodeCurrent(),
        visitorId: getReferralVisitorId(),
        business,
      }),
    );
  } catch {
    /* analytics is best-effort */
  }
};

/**
 * Engine behind `<ReferralProvider>`; prefer `useReferral()` in
 * components. Mount once near the root of a web app to capture `?r=CODE`.
 *
 * On mount (client only):
 * 1. Reads `?r=` from the current URL. When present, stores it: the
 *    original `hero_r` cookie is first-touch, `hero_r_last` follows
 *    every capture. Mints `hero_vid` if missing.
 * 2. Registers the referral PostHog super-properties
 *    (`referral_code`, `referral_code_current`, `referral_visitor_id`)
 *    so every autocaptured pageview / click carries the code, and sends
 *    a `referral_captured` product event.
 * 3. Posts one `view` hit for the code that arrived on the URL, carrying
 *    the PostHog `distinct_id` so the backend can alias the anonymous
 *    session to the person it later identifies. A page reached without
 *    `?r=` posts nothing, even if a cookie exists — a view counts the
 *    link being opened, not every later page.
 *
 * Returns `trackClick(target)` for CTAs ("Book demo", "WhatsApp",
 * "Sign up") so the admin funnel shows view → click → lead. It uses
 * the stored original code, so it works on pages reached after the
 * first one. It posts the ledger hit only — the CTA's own product
 * event (`landing_signup_click`, …) is the PostHog side, and it already
 * carries the referral super-properties.
 *
 * Best-effort throughout: a failed POST is swallowed. The same rules
 * are implemented for external sites by `hero-r.js`.
 */
export const useReferralCapture = (options: UseReferralCaptureOptions) => {
  const { business, language, trackView = true } = options;
  const api = useApi();
  const viewSent = useRef(false);

  const buildContext = useCallback(
    (target?: string, code?: string): TrackingContext => {
      if (typeof window === 'undefined') return { business };
      const params = new URLSearchParams(window.location.search);
      const original = getReferralCode();
      const distinctId = posthogDistinctId();
      return {
        url: window.location.href.slice(0, 2048),
        host: window.location.hostname,
        ...(document.referrer ? { referrer: document.referrer.slice(0, 2048) } : {}),
        ...(target ? { target } : {}),
        business,
        ...(language ? { language } : {}),
        ...(original && code && original !== code ? { originalCode: original } : {}),
        ...(distinctId ? { posthogDistinctId: distinctId } : {}),
        ...(readUtm(params) ? { utm: readUtm(params) } : {}),
      };
    },
    [business, language],
  );

  const send = useCallback(
    (code: string, type: 'view' | 'click', target?: string) => {
      void api.referral
        .track({
          code,
          type,
          visitorId: getReferralVisitorId(),
          context: buildContext(target, code),
        })
        .catch(() => undefined);
    },
    [api, buildContext],
  );

  useEffect(() => {
    if (typeof window === 'undefined' || viewSent.current) return;
    viewSent.current = true;
    const fromUrl = readReferralCodeFromSearch(window.location.search);
    if (!fromUrl) {
      // Returning visitor without `?r=`: keep PostHog stamped anyway.
      registerReferralSuperProperties(business);
      return;
    }
    const stored = storeReferralCode(fromUrl);
    getReferralVisitorId();
    registerReferralSuperProperties(business);
    capture({
      event: 'referral_captured',
      payload: {
        referral_code: stored.original,
        referral_code_current: fromUrl,
        referral_business: business,
        first_touch: stored.firstTouch,
      },
    });
    if (trackView) send(fromUrl, 'view');
  }, [send, trackView, business]);

  const trackClick = useCallback(
    (target: string) => {
      const original = getReferralCode();
      if (!original) return;
      // The hit belongs to the link that brought the visitor this
      // session (same code the page `view` was stored under); the
      // first-touch code stays on the PostHog event for attribution.
      const code = getReferralCodeCurrent() ?? original;
      // Ledger only. The CTA's own product event (`landing_signup_click`,
      // `landing_whatsapp_cta_click`, …) is the PostHog record of the
      // click, already carrying the referral super-properties — a
      // second `referral_*` event for the same tap would double-count.
      send(code, 'click', target);
    },
    [send, business],
  );

  return { trackClick };
};
